import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import {
  CandidateDetail,
  JobPostingDetail,
  PaginatedResponse,
  RecruitmentProcessCollection,
} from '../types';
import { EmailFormValues } from './EmailForm';
import SendAdditionalQuestionsModal, {
  SendAdditionalQuestionsModalProps,
} from './SendAdditionalQuestionsModal';

interface PartialSendAdditionalQuestionsModalProps
  extends Omit<SendAdditionalQuestionsModalProps, 'helmetTitle' | 'hasCandidatesThatAnswered'> {
  candidateIds: CandidateDetail['id'][];
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (data: void, variables: EmailFormValues, context: unknown) => void | Promise<unknown>;
}

const PartialSendAdditionalQuestionsModal = ({
  candidateIds,
  onSuccess,
  jobPostingId,
  ...props
}: PartialSendAdditionalQuestionsModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const mutation = useAPIMutation(
    async (
      { message, subject, attachments, enableReplyTo, fromName, replyTo }: EmailFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      for (const candidateId of candidateIds) {
        const recruitmentProcess = await queryClient.fetchQuery<
          PaginatedResponse<RecruitmentProcessCollection>
        >([
          '/recruitment-processes/',
          {
            params: {
              candidate: candidateId,
              has_answered_questions: false,
              job_posting: jobPostingId,
            },
          },
        ]);

        if (recruitmentProcess.count > 0) {
          await api.post('/email-communications/', {
            from_name: fromName,
            candidate: candidateId,
            job_posting: jobPostingId,
            subject,
            message,
            ...(enableReplyTo ? { reply_to: replyTo } : {}),
            type: 'ADDITIONAL_QUESTIONS',
            attachments: attachmentIds,
          });
        }
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-history/');
      },
    }
  );

  const hasCandidatesThatAnswered = async () => {
    for (const candidateId of candidateIds) {
      const recruitmentProcess = await queryClient.fetchQuery<
        PaginatedResponse<RecruitmentProcessCollection>
      >([
        '/recruitment-processes/',
        {
          params: {
            candidate: candidateId,
            has_answered_questions: true,
            job_posting: jobPostingId,
          },
        },
      ]);

      if (recruitmentProcess.count > 0) {
        return true;
      }
    }
    return false;
  };

  return (
    <SendAdditionalQuestionsModal
      title={
        candidateIds.length > 1 ? (
          <span>
            <Trans>Enviar perguntas de pré-requisito para</Trans>{' '}
            <strong>
              {candidateIds.length} <Trans>candidato(s)</Trans>
            </strong>
          </span>
        ) : (
          <span>
            <Trans>Enviar perguntas de pré-requisito</Trans>
          </span>
        )
      }
      helmetTitle={
        candidateIds.length > 1
          ? `${t('Enviar perguntas de pré-requisito para')} ${candidateIds.length} ${t(
              'candidato(s)'
            )}`
          : t('Enviar perguntas de pré-requisito')
      }
      hasCandidatesThatAnswered={hasCandidatesThatAnswered}
      loading={mutation.isLoading}
      onFinish={(values) => {
        mutation.mutate(values);
      }}
      jobPostingId={jobPostingId}
      {...props}
    />
  );
};

export default PartialSendAdditionalQuestionsModal;
